/**
 * How an outstanding invitation reads in the sharing modal.
 *
 * Its own module, and not a named export of ProjectSharingModal, so a test
 * can reach this pure function without dragging in the component's service
 * imports — those reach `src/firebase.js`, which calls `getAuth` at import
 * time and throws `auth/invalid-api-key` wherever the REACT_APP_FIREBASE_*
 * vars are absent. CI has no .env, so importing the component to test a
 * string formatter passes locally and fails there.
 */

/** A Firestore timestamp as it arrives over a callable, as a local date. */
const timestampToDate = (ts) =>
  ts?._seconds ? new Date(ts._seconds * 1000) : null;

/**
 * What an outstanding invitation's line says.
 *
 * An expired invitation used to be invisible here — the query asked only for
 * `pending`, so once someone tried to accept a stale link and the server
 * restamped it, the row disappeared and the owner saw nothing at all. It is
 * the one state they need to act on, so it says so and offers Resend.
 */
export const describeInvitation = (invitation) => {
  const invitedAt = timestampToDate(invitation.invitedAt);
  const expiresAt = timestampToDate(invitation.expiresAt);

  if (invitation.status === 'expired') {
    return expiresAt
      ? `Expired ${expiresAt.toLocaleDateString()} — not accepted`
      : 'Expired — not accepted';
  }

  const invited = invitedAt
    ? `Invited ${invitedAt.toLocaleDateString()}`
    : 'Invited';
  return expiresAt
    ? `${invited} — expires ${expiresAt.toLocaleDateString()}`
    : invited;
};
